/**
 * The match percentage shown under a tagged face.
 *
 * WHAT THE NUMBER IS. Seraph matches a face embedding against the gallery and
 * maps the winning cosine distance through a piecewise-linear curve. That
 * mapped value (`FaceEmbedding.match_confidence`) is what renders here. It is
 * NOT a probability and is not calibrated against labelled data — 92% means
 * "cosine distance was about 0.08", not "92% chance this is the right
 * person". The raw distance is stored alongside it (`match_distance`)
 * precisely so a future calibration can re-derive every historical percentage
 * without re-running matching.
 *
 * WHAT IT IS NOT, AND WHAT THIS FILE EXISTS TO STOP. It is not
 * `FaceEmbedding.score`. That field is the DETECTION score — seraph
 * `steps/persistence.py:109` fills it from `face.detection_score`, and it
 * answers "how sure are we that this rectangle is a face". It says nothing
 * whatsoever about WHICH person that face belongs to. The caption stack used
 * to read `face.score` and print it as a percentage directly beneath the
 * player's name, where the only available reading is identity confidence.
 * Detection scores cluster in 0.85–0.99, so it rendered as a confident 95–100%
 * on essentially every face, including the wrong ones — a number that could
 * only ever agree with the editor. It survived a long time because it was
 * plausible, not because it was right.
 *
 * WHY AMBIGUITY IS SEPARATE. The server used to fold a 0.7 penalty into the
 * confidence itself whenever the runner-up was close. That made the scalar
 * NON-MONOTONIC: a near-perfect match with a close second rendered 63% while a
 * clearly worse but unambiguous match rendered 70% — the better match showed
 * the lower number. The penalty is gone from the stored value, and the
 * ambiguity is carried as its own margin (`match_ambiguity_margin`, being
 * second_best minus best) so it can be SHOWN rather than silently averaged
 * into something else.
 *
 * TWO PROPERTIES OF THE SCALE, both deliberate and both worth knowing before
 * reading meaning into small differences:
 *   - The slope kinks at distance 0.35. Below it the curve falls ~1 point per
 *     unit distance, above it ~2.5. So 70 vs 65 and 65 vs 52 represent the
 *     SAME change in distance.
 *   - It floors at 40%. Nothing worse is ever returned as a match at all, so
 *     40% is not "quite poor", it is the worst that can ever appear. Anything
 *     genuinely poor shows no face match, not a low percentage.
 *
 * NULL IS NOT ZERO AND NOT ONE HUNDRED. A face carries no percentage at all
 * when it was tagged by hand (a human tag is not a match, and printing 100%
 * would invent data), when matching never ran, or when it predates the field
 * — there is no backfill. All three are the same state: we did not measure
 * this. It renders as an absence, never as a number and never as a bar.
 *
 * Kept in lockstep with `matrix-app/src/utils/matchConfidence.ts` — same
 * rules, same threshold, same rendered strings, so a percentage means the
 * same thing whichever client an editor is looking at.
 */

/** Below this margin between best and second-best, the match is a close call. */
export const AMBIGUOUS_MARGIN = 0.05;

/**
 * The identity match resolved into what a card can draw, or null when there
 * is nothing honest to draw.
 *
 * This is the single decision point: `matchConfidenceLine` below and the bar
 * in CaptionOrderStack both read it, so the text and the bar cannot disagree
 * about whether a face has a measurement.
 *
 * @param {{match_confidence?: number|null,
 *          match_ambiguity_margin?: number|null}|null|undefined} face
 * @param {boolean} isManual Editor-tagged entry, which has no match to report.
 * @returns {{pct: number, ambiguous: boolean}|null}
 */
export function matchConfidenceState(face, isManual = false) {
  if (isManual || !face) return null;

  const conf = face.match_confidence;
  // Non-finite covers null, undefined and a NaN arriving from a malformed
  // payload. A NaN is not a zero-confidence match, it is a missing one.
  if (typeof conf !== 'number' || !Number.isFinite(conf)) return null;

  const pct = Math.round(Math.max(0, Math.min(1, conf)) * 100);

  const margin = face.match_ambiguity_margin;
  const ambiguous =
    margin != null && Number.isFinite(margin) && margin < AMBIGUOUS_MARGIN;

  return { pct, ambiguous };
}

/**
 * The confidence line for a face card, or null when there is nothing honest
 * to show. Byte-for-byte the same strings the app renders.
 *
 * @param {{match_confidence?: number|null,
 *          match_ambiguity_margin?: number|null}|null|undefined} face
 * @param {boolean} isManual
 * @returns {string|null}
 */
export function matchConfidenceLine(face, isManual = false) {
  const state = matchConfidenceState(face, isManual);
  if (!state) return null;
  return state.ambiguous ? `${state.pct}% · CLOSE` : `${state.pct}%`;
}
